import { message, Modal } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useAuth } from '../../contexts/Auth'
import MixpanelTracker from '../../services/track/mixpanel'
import Finish from './Finish'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'
import Step5 from './Step5'
import Stepper from './Stepper'

const STORAGE_PREFIX = process.env.REACT_APP_STORAGE_PREFIX || 'haup-submission'

interface FormData {
  selectedCategory: number | null
  selectedSubcategories: number[]
  otherActivitiesChecked: boolean
  otherActivityTypeNames: string[]
  selectedExperience: string
  uploadedLicense: string | null
  activityName: string
  destination: {
    name: string
    address: string
    lat: number
    long: number
    country: string
  } | null
  startTime: { hour: string; minute: string; period: string }
  additionalTimes: Array<{ hour: string; minute: string; period: string }>
  transportModes: string[]
  tourType: string
  maxPeople: string
  activityHighlights: string[]
  activitySummary: string
  uploadedImages: string[]
  packageName: string
  shortDescription: string
  whatsIncluded: string[]
  whatsNotIncluded: string[]
  importantInfo: string[]
  meetingPoints: Array<{
    title: string
    subtitle: string
    lat: number | null
    long: number | null
    details: string
  }>
  accordionOpen: boolean
  scheduleAccordionOpen: boolean
  itineraries: Array<{
    title: string
    duration: string
    details: string
    hasPhoto: boolean
    images: string[]
  }>
  schedule: string
  scheduleHasPhoto: boolean
  scheduleImages: string[]
  pricePerPerson: string
  pricePerChild: string
  freeForChild: boolean
  extraExpenses: string
  discount: string
  currency: string
  discountType: string
  instantBooking: boolean
  refundableBooking: boolean
  changeDateAvailable: boolean
  timeslots: Array<{
    day: string
    startTime: string
    endTime: string
    is24Hours: boolean
  }>
}

interface StepTutorialProps {
  step: number
  title: string
  description: string
  icon: React.ReactNode
  onNext: () => void
  onBack?: () => void
}

const StepTutorial: React.FC<StepTutorialProps> = ({
  step,
  title,
  description,
  icon,
  onNext,
  onBack,
}) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  return (
    <div
      style={{
        backgroundColor: '#fff',
        padding: '24px 16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
        boxSizing: 'border-box',
        fontFamily: 'Poppins, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: '100vh',
        transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      }}
    >
      <div
        style={{
          fontSize: '80px',
          color: '#0048B3',
          marginBottom: '24px',
        }}
      >
        {icon}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <p
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#667085',
            textTransform: 'uppercase',
            margin: '0',
          }}
        >
          Step {step}
        </p>
        <h2
          style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#212427',
            margin: '8px 0 0',
          }}
        >
          {title}
        </h2>
      </div>
      <p
        style={{
          fontSize: '16px',
          lineHeight: '1.5',
          color: '#667085',
          textAlign: 'left',
          marginBottom: '32px',
        }}
      >
        {description}
      </p>

      <div
        style={{
          display: 'flex',
          justifyContent: onBack ? 'space-between' : 'flex-end',
          width: '100%',
          marginTop: '30px',
          gap: '16px',
        }}
      >
        {onBack && (
          <button
            onClick={onBack}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#667085',
              border: '1px solid #D6DAE0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Back
          </button>
        )}

        <button
          onClick={onNext}
          style={{
            flex: '1',
            padding: '12px 24px',
            backgroundColor: '#0048B3',
            color: 'white',
            border: 'none',
            borderRadius: '24px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '16px',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          Next
        </button>
      </div>
    </div>
  )
}

const tutorials = {
  1: {
    title: 'Share your experience',
    description:
      "You'll be asked to provide information about your experience related to this activity category, including how many years you've been guiding or involved in this field. You'll also specify which languages you can speak while hosting this activity. This helps guests understand your background and feel more comfortable, knowing they'll be able to communicate with you during the activity.",
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/5q5terdjCC6k2ZZzz5YNDQxhEhH3gJMvetFw9bjXNbFyuMwoJXQKSNjv7aFM525sFcTL7pFYiiLnUd8ErpPXhP8a.png"
        alt="Step 1 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
  2: {
    title: 'Highlight key activity info',
    description:
      "You'll be asked to enter key information such as the destination, duration, available start times, mode of transport, and tour type. These details help travelers get a clear picture of how your activity is structured and what the experience will involve. By sharing this upfront, you make it easier for travelers to plan their trip, manage expectations, and choose the experience that best fits their interests and schedule.",
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/33fhHtRiB6XErmS58g4yD12U7RQDdbNuGHikSSmX14xkjcWHKJf4gC6Kj7Phf8NLqGeuLPU4WcaFohrrp3QXGjpn.png"
        alt="Step 2 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
  3: {
    title: 'Add activity overview',
    description:
      "You'll be asked to write a short description that captures the essence of your activity—what it's about, who it's for, and why it stands out. This is your chance to highlight what makes the experience special, whether it's the local insight you provide, the hidden gems you reveal, or the memorable moments guests can expect. A compelling summary helps attract the right audience and encourages travelers to learn more and book with confidence.",
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/29jfbMh9YgbDuaXDonSQpuQALcEA3E5L1V1vjAQz52aXGfMru5woFS9A9YdmWP1ffg2ryQBnm4H1Ydkn2AgN74gV.png"
        alt="Step 3 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
  4: {
    title: 'Create packages',
    description:
      "Whether it's different time slots, group sizes, activity durations, or package levels, providing flexible options helps travelers book with confidence. For example, you might offer a morning and evening session, or a standard and premium experience with added perks. Be clear about what each option includes and how they differ to help guests pick the right one for their needs.",
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/3YerBQjj9DBYk33aYXh5ubdZv4sYC1JNonq19z1yfH7N2fKNgxnwQP9LuChaDon2wmMPZaRektz7m9AMhpyd1XJ6.png"
        alt="Step 4 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
  5: {
    title: 'Manage booking preferences',
    description:
      'Choose whether you want to accept bookings instantly or review each request manually. Set your cancellation policy—refundable or non-refundable—and decide if guests can reschedule their experience. These preferences help you stay in control while offering flexibility that suits your activity and availability.',
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/3uvbzh8nGWfVjf2HMUm4b8o69iaS3ydUfvQaCvdC8k5obRe7SCkXgKsvKubSVNMSrdg7vE5nGd6j2r26oSLmWYuo.png"
        alt="Step 5 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
}

const mapApiDataToFormData = (apiData: any): FormData => {
  const initialFormData: FormData = {
    selectedCategory: null,
    selectedSubcategories: [],
    otherActivitiesChecked: false,
    otherActivityTypeNames: [],
    selectedExperience: '',
    uploadedLicense: null,
    activityName: '',
    destination: null,
    startTime: { hour: '', minute: '', period: 'AM' },
    additionalTimes: [],
    transportModes: [],
    tourType: '',
    maxPeople: '',
    activityHighlights: [''],
    activitySummary: '',
    uploadedImages: [],
    packageName: '',
    shortDescription: '',
    whatsIncluded: [''],
    whatsNotIncluded: [''],
    importantInfo: [''],
    meetingPoints: [{ title: '', subtitle: '', lat: null, long: null, details: '' }],
    accordionOpen: false,
    scheduleAccordionOpen: false,
    itineraries: [{ title: '', duration: '', details: '', hasPhoto: false, images: [] }],
    schedule: '',
    scheduleHasPhoto: false,
    scheduleImages: [],
    pricePerPerson: '',
    pricePerChild: '',
    freeForChild: false,
    extraExpenses: '',
    discount: '',
    currency: 'THB',
    discountType: '%',
    instantBooking: false,
    refundableBooking: false,
    changeDateAvailable: false,
    timeslots: [],
  }

  const parseTime = (timeStr: string) => {
    const [time, period] = timeStr.split(' ')
    if (!time || !period) return { hour: '', minute: '', period: 'AM' }
    const [hour, minute] = time.split(':')
    return { hour, minute, period }
  }

  const transportModes = apiData.transport_mode
    ? apiData.transport_mode.split(',').map((s: string) => s.trim())
    : []
  const tourType = apiData.tour_type?.toLowerCase().includes('private') ? 'private' : 'group'
  const startTimes = apiData.start_times ? apiData.start_times.map(parseTime) : []
  const firstStartTime = startTimes.length > 0 ? startTimes[0] : initialFormData.startTime
  const additionalTimes =
    startTimes.length > 1 ? startTimes.slice(1) : initialFormData.additionalTimes

  const highlights = Array.isArray(apiData.highlight)
    ? apiData.highlight.map((h: any) => h.title)
    : ['']

  const destination = apiData.locations?.length > 0 ? apiData.locations[0] : null
  const images = apiData.gallery?.length > 0 ? apiData.gallery.map((g: any) => g.url) : []

  const firstOption = apiData.options?.[0]

  let whatsIncluded: string[] = ['']
  let whatsNotIncluded: string[] = ['']
  let importantInfo: string[] = ['']
  let meetingPoints = [{ title: '', subtitle: '', lat: null, long: null, details: '' }]
  let itineraries: any[] = [{ title: '', duration: '', details: '', hasPhoto: false, images: [] }]

  if (firstOption?.activityFeatures) {
    const includedFeature = firstOption.activityFeatures.find((f: any) => f.type === 'INCLUDE')
    if (includedFeature?.description?.included) {
      whatsIncluded = includedFeature.description.included.map((d: any) => d.title)
    }
    if (includedFeature?.description?.excluded) {
      whatsNotIncluded = includedFeature.description.excluded.map((d: any) => d.title)
    }

    const importantInfoFeature = firstOption.activityFeatures.find(
      (f: any) => f.type === 'IMPORTANT_INFO',
    )
    if (importantInfoFeature?.description) {
      importantInfo = importantInfoFeature.description.map((d: any) => d.title)
    }

    const meetingPointFeature = firstOption.activityFeatures.find(
      (f: any) => f.type === 'MEETING_POINT',
    )
    if (meetingPointFeature?.description) {
      meetingPoints = meetingPointFeature.description.map((d: any) => ({
        title: d.title,
        subtitle: d.subtitle,
        lat: d.lat,
        long: d.long,
        details: d.details || '',
      }))
    }

    const itineraryFeature = firstOption.activityFeatures.find((f: any) => f.type === 'ITINERARY')
    if (itineraryFeature?.description) {
      itineraries = itineraryFeature.description.map((d: any) => ({
        title: d.title,
        duration: d.duration,
        details: d.details,
        hasPhoto: d.images?.length > 0,
        images: d.images || [],
      }))
    }
  }

  const packageName = firstOption?.name || ''
  const shortDescription = firstOption?.subtitle || ''
  const schedule = firstOption?.schedule?.subtitles?.[0] || ''
  const scheduleImages = firstOption?.schedule?.images || []
  const pricePerPerson = firstOption?.adult_price?.toString() || ''
  const pricePerChild = firstOption?.child_price?.toString() || ''
  const freeForChild = firstOption?.child_price === '0.00' || firstOption?.child_price === 0
  const discount = firstOption?.discount_price?.toString() || ''
  const extraExpenses = firstOption?.extra_expenses || ''
  const timeslots = firstOption?.activityTimesSlots || []

  const otherActivityTypeNames = Array.isArray(apiData.other_activity_type)
    ? apiData.other_activity_type
    : apiData.other_activity_type && Object.keys(apiData.other_activity_type).length > 0
    ? Object.values(apiData.other_activity_type)
    : []

  const otherActivitiesChecked = otherActivityTypeNames.length > 0

  return {
    ...initialFormData,
    selectedCategory: apiData.activity_main_category_id || null,
    selectedSubcategories: apiData.activity_type_ids || [],
    otherActivitiesChecked: otherActivitiesChecked,
    otherActivityTypeNames: otherActivityTypeNames as string[],
    selectedExperience: apiData.experience?.duration || '',
    uploadedLicense: apiData.experience?.license || null,
    activityName: apiData.name || '',
    destination: destination,
    startTime: firstStartTime,
    additionalTimes: additionalTimes,
    transportModes: transportModes,
    tourType: tourType,
    maxPeople: apiData.maximum_people?.toString() || '',
    activityHighlights: highlights,
    activitySummary: apiData.description || '',
    uploadedImages: images,
    packageName,
    shortDescription,
    whatsIncluded: whatsIncluded.length > 0 ? whatsIncluded : [''],
    whatsNotIncluded: whatsNotIncluded.length > 0 ? whatsNotIncluded : [''],
    importantInfo: importantInfo.length > 0 ? importantInfo : [''],
    meetingPoints:
      meetingPoints.length > 0
        ? meetingPoints
        : [{ title: '', subtitle: '', lat: null, long: null, details: '' }],
    itineraries:
      itineraries.length > 0
        ? itineraries
        : [{ title: '', duration: '', details: '', hasPhoto: false, images: [] }],
    schedule,
    scheduleHasPhoto: scheduleImages.length > 0,
    scheduleImages,
    pricePerPerson,
    pricePerChild,
    freeForChild: freeForChild,
    extraExpenses,
    discount,
    currency: 'THB',
    discountType: '%',
    instantBooking: apiData.is_instant_confirmation || false,
    refundableBooking: apiData.is_free_cancellation || false,
    changeDateAvailable: apiData.is_date_changable || false,
    timeslots: timeslots,
  }
}

interface StepProgress {
  step1: number
  step2: number
  step3: number
  step4: number
  step5: number
}

const generateRandomId = () => Math.floor(10000 + Math.random() * 90000)

const ExpertSubmissionPage: React.FC = () => {
  const { path } = useRouteMatch()
  const history = useHistory()
  const location = useLocation()
  const { userLoggedIn } = useAuth()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [draftId, setDraftId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [currentId, setCurrentId] = useState<string>(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const urlDraftId = urlParams.get('draft_id')
    if (urlDraftId) {
      return urlDraftId
    }
    const storedTempId = sessionStorage.getItem(`${STORAGE_PREFIX}_temp_id`)
    if (storedTempId) {
      return storedTempId
    }
    const newTempId = generateRandomId().toString()
    sessionStorage.setItem(`${STORAGE_PREFIX}_temp_id`, newTempId)
    return newTempId
  })

  const [formData, setFormData] = useState<FormData>({
    selectedCategory: null,
    selectedSubcategories: [],
    otherActivitiesChecked: false,
    otherActivityTypeNames: [],
    selectedExperience: '',
    uploadedLicense: null,
    activityName: '',
    destination: null,
    startTime: { hour: '', minute: '', period: 'AM' },
    additionalTimes: [],
    transportModes: [],
    tourType: '',
    maxPeople: '',
    activityHighlights: [''],
    activitySummary: '',
    uploadedImages: [],
    packageName: '',
    shortDescription: '',
    whatsIncluded: [''],
    whatsNotIncluded: [''],
    importantInfo: [''],
    meetingPoints: [{ title: '', subtitle: '', lat: null, long: null, details: '' }],
    accordionOpen: false,
    scheduleAccordionOpen: false,
    itineraries: [{ title: '', duration: '', details: '', hasPhoto: false, images: [] }],
    schedule: '',
    scheduleHasPhoto: false,
    scheduleImages: [],
    pricePerPerson: '',
    pricePerChild: '',
    freeForChild: false,
    extraExpenses: '',
    discount: '',
    currency: 'THB',
    discountType: '%',
    instantBooking: false,
    refundableBooking: false,
    changeDateAvailable: false,
    timeslots: [],
  })

  const [tutorialShown, setTutorialShown] = useState<{ [key: number]: boolean }>(() => {
    try {
      const storageKey = `${STORAGE_PREFIX}_tutorialShown_session`
      const storedState = sessionStorage.getItem(storageKey)
      return storedState
        ? JSON.parse(storedState)
        : { 1: false, 2: false, 3: false, 4: false, 5: false }
    } catch (e) {
      console.error('Failed to parse tutorialShown from sessionStorage', e)
      return { 1: false, 2: false, 3: false, 4: false, 5: false }
    }
  })

  const [highestStepReached, setHighestStepReached] = useState<number>(() => {
    try {
      const storageKey = `${STORAGE_PREFIX}_highestStep_session`
      const storedValue = sessionStorage.getItem(storageKey)
      return storedValue ? parseInt(storedValue) : 1
    } catch (e) {
      console.error('Failed to parse highestStepReached from sessionStorage', e)
      return 1
    }
  })

  const [stepProgress, setStepProgress] = useState<StepProgress>(() => {
    try {
      const storedProgress = sessionStorage.getItem(`${STORAGE_PREFIX}_stepProgress`)
      return storedProgress
        ? JSON.parse(storedProgress)
        : { step1: 0, step2: 0, step3: 0, step4: 0, step5: 0 }
    } catch (e) {
      console.error('Failed to parse stepProgress from sessionStorage', e)
      return { step1: 0, step2: 0, step3: 0, step4: 0, step5: 0 }
    }
  })

  useEffect(() => {
    try {
      const storageKey = `${STORAGE_PREFIX}_tutorialShown_session`
      sessionStorage.setItem(storageKey, JSON.stringify(tutorialShown))
    } catch (e) {
      console.error('Failed to save tutorialShown to sessionStorage', e)
    }
  }, [tutorialShown])

  useEffect(() => {
    try {
      sessionStorage.setItem(`${STORAGE_PREFIX}_stepProgress`, JSON.stringify(stepProgress))
    } catch (e) {
      console.error('Failed to save stepProgress to sessionStorage', e)
    }
  }, [stepProgress])

  useEffect(() => {
    try {
      const storageKey = `${STORAGE_PREFIX}_highestStep_session`
      sessionStorage.setItem(storageKey, highestStepReached.toString())
    } catch (e) {
      console.error('Failed to save highestStepReached to sessionStorage', e)
    }
  }, [highestStepReached])

  useEffect(() => {
    const pathSegments = location.pathname.split('/')
    const stepSegment = pathSegments.find((segment) => segment.startsWith('step'))
    const stepNumber = stepSegment ? parseInt(stepSegment.replace('step', '')) : 1

    if (!isNaN(stepNumber)) {
      setCurrentStep(stepNumber)
    }

    const urlParams = new URLSearchParams(location.search)
    const urlDraftId = urlParams.get('draft_id')

    if (urlDraftId) {
      const parsedDraftId = parseInt(urlDraftId)
      if (!isNaN(parsedDraftId)) {
        setDraftId(parsedDraftId)
        setCurrentId(urlDraftId)
      }
    } else {
      setDraftId(null)
      setFormData({
        selectedCategory: null,
        selectedSubcategories: [],
        otherActivitiesChecked: false,
        otherActivityTypeNames: [],
        selectedExperience: '',
        uploadedLicense: null,
        activityName: '',
        destination: null,
        startTime: { hour: '', minute: '', period: 'AM' },
        additionalTimes: [],
        transportModes: [],
        tourType: '',
        maxPeople: '',
        activityHighlights: [''],
        activitySummary: '',
        uploadedImages: [],
        packageName: '',
        shortDescription: '',
        whatsIncluded: [''],
        whatsNotIncluded: [''],
        importantInfo: [''],
        meetingPoints: [{ title: '', subtitle: '', lat: null, long: null, details: '' }],
        accordionOpen: false,
        scheduleAccordionOpen: false,
        itineraries: [{ title: '', duration: '', details: '', hasPhoto: false, images: [] }],
        schedule: '',
        scheduleHasPhoto: false,
        scheduleImages: [],
        pricePerPerson: '',
        pricePerChild: '',
        freeForChild: false,
        extraExpenses: '',
        discount: '',
        currency: 'THB',
        discountType: '%',
        instantBooking: false,
        refundableBooking: false,
        changeDateAvailable: false,
        timeslots: [],
      })
      setStepProgress({ step1: 0, step2: 0, step3: 0, step4: 0, step5: 0 })
    }

    MixpanelTracker.track('Activity Submission Page Viewed', {
      'Activity ID': urlDraftId || 'new',
      'Current Step': stepNumber,
    })
  }, [location.pathname, location.search])

  useEffect(() => {
    if (currentStep > 1) {
      setTutorialShown((prev) => {
        const newState = { ...prev }
        for (let i = 1; i < currentStep; i++) {
          newState[i] = true
        }
        return newState
      })
    }
  }, [currentStep])

  useEffect(() => {
    try {
      const storageKey = `${STORAGE_PREFIX}_tutorialShown_${currentId}`
      const storedState = sessionStorage.getItem(storageKey)
      if (storedState) {
        setTutorialShown(JSON.parse(storedState))
      } else {
        setTutorialShown({ 1: false, 2: false, 3: false, 4: false, 5: false })
      }
    } catch (e) {
      console.error('Failed to load tutorialShown from sessionStorage', e)
    }
  }, [currentId])

  useEffect(() => {
    const fetchDraftData = async () => {
      if (!draftId || !userLoggedIn?.userToken) return

      setIsLoading(true)

      setTimeout(async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_TS_RESTFUL_API_URL}/activity/expert-submission/${draftId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: userLoggedIn.userToken,
                project: 'haup-stable',
              },
            },
          )

          const result = await response.json()

          if (!response.ok || !result.result) {
            throw new Error(result.message || 'Failed to fetch draft data')
          }

          setFormData(mapApiDataToFormData(result.data))

          const newStepProgress: StepProgress = {
            step1: 0,
            step2: 0,
            step3: 0,
            step4: 0,
            step5: 0,
          }
          result.data.completed_steps.forEach((step: number) => {
            if (step >= 1 && step <= 5) {
              newStepProgress[`step${step}` as keyof StepProgress] = 100
            }
          })
          setStepProgress(newStepProgress)
        } catch (error: any) {
          console.error('Failed to fetch draft data:', error)
          message.error(`Failed to fetch draft data: ${error.message}`)
        } finally {
          setIsLoading(false)
        }
      }, 1000)
    }

    fetchDraftData()
  }, [draftId, userLoggedIn?.userToken])

  const updateFormData = useCallback((stepData: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...stepData }))
  }, [])

  const handleStepProgress = useCallback((step: number, progressPercentage: number) => {
    setStepProgress((prev) => ({
      ...prev,
      [`step${step}`]: progressPercentage,
    }))
  }, [])

  const handleStep1Progress = useCallback(
    (progress: number) => handleStepProgress(1, progress),
    [handleStepProgress],
  )

  const handleStep2Progress = useCallback(
    (progress: number) => handleStepProgress(2, progress),
    [handleStepProgress],
  )

  const handleStep3Progress = useCallback(
    (progress: number) => handleStepProgress(3, progress),
    [handleStepProgress],
  )

  const handleStep4Progress = useCallback(
    (progress: number) => handleStepProgress(4, progress),
    [handleStepProgress],
  )

  const handleStep5Progress = useCallback(
    (progress: number) => handleStepProgress(5, progress),
    [handleStepProgress],
  )

  const createDraft = useCallback(async () => {
    if (!userLoggedIn?.userToken) {
      throw new Error('Authentication token is missing. Please log in again.')
    }

    const response = await fetch(
      `${process.env.REACT_APP_TS_RESTFUL_API_URL}/activity/expert-submission`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: userLoggedIn.userToken,
          project: 'haup-stable',
        },
        body: JSON.stringify({}),
      },
    )

    const result = await response.json()
    if (!response.ok || !result.result || !result.data?.id) {
      throw new Error(result.message || 'Failed to create draft')
    }
    return result.data.id
  }, [userLoggedIn?.userToken])

  const saveStepData = useCallback(
    async (stepNumber: number, payload: any, targetDraftId?: number | null) => {
      if (!userLoggedIn?.userToken) {
        throw new Error('Authentication token is missing.')
      }

      const useDraftId = targetDraftId || draftId
      if (!useDraftId) {
        throw new Error('Draft ID is missing.')
      }

      const response = await fetch(
        `${process.env.REACT_APP_TS_RESTFUL_API_URL}/activity/expert-submission/draft/${useDraftId}/step/${stepNumber}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: userLoggedIn.userToken,
            project: 'haup-stable',
          },
          body: JSON.stringify(payload),
        },
      )

      const result = await response.json()

      if (!response.ok || !result.result) {
        const errorMessage = result.error?.message || `Failed to save step ${stepNumber} data`
        throw new Error(errorMessage)
      }
      return result
    },
    [userLoggedIn?.userToken, draftId],
  )

  const buildStep1Payload = useCallback(() => {
    return {
      main_category_id: formData.selectedCategory,
      activity_type_ids: formData.selectedSubcategories,
      other_activity_type: formData.otherActivityTypeNames,
      experience: {
        duration: formData.selectedExperience,
        license: formData.uploadedLicense || '',
      },
    }
  }, [formData])

  const buildStep2Payload = useCallback(() => {
    const formatTime = (timeObj: { hour: string; minute: string; period: string }) => {
      if (!timeObj.hour || !timeObj.minute) return ''
      return `${timeObj.hour}:${timeObj.minute} ${timeObj.period}`
    }

    const startTimes = [
      formatTime(formData.startTime),
      ...formData.additionalTimes.map(formatTime),
    ].filter((time) => time !== '')

    const locations = formData.destination ? [formData.destination] : []

    let maximumPeople = 1
    if (formData.maxPeople && formData.maxPeople.trim() !== '') {
      const parsed = parseInt(formData.maxPeople)
      if (!isNaN(parsed) && parsed > 0) {
        maximumPeople = parsed
      }
    }

    const activityName =
      formData.activityName.trim() === '' ? 'Draft Activity' : formData.activityName

    return {
      name: activityName,
      locations: locations,
      start_times: startTimes,
      transport_mode: formData.transportModes.join(', '),
      tour_type: formData.tourType === 'group' ? 'Group tour' : 'Private tour',
      maximum_people: maximumPeople,
    }
  }, [formData])

  const buildStep3Payload = useCallback(() => {
    return {
      highlight: formData.activityHighlights.filter((h) => h.trim() !== ''),
      description: formData.activitySummary || '',
      gallery: (formData.uploadedImages || []).map((url, index) => ({
        url,
        type: 'image',
        sequence: index + 1,
      })),
    }
  }, [formData])

  const buildStep4Payload = useCallback(() => {
    const scheduleText = formData.schedule?.trim()
    const scheduleImages = formData.scheduleImages || []

    const schedulePayload =
      scheduleText || scheduleImages.length > 0
        ? {
            images: scheduleImages,
            subtitles: scheduleText ? [scheduleText] : [],
          }
        : null

    const timeslotsPayload = formData.timeslots.length > 0 ? formData.timeslots : null
    const extraExpensesPayload =
      formData.extraExpenses.trim() === '' ? null : formData.extraExpenses

    const payload = {
      options: [
        {
          name: formData.packageName,
          subtitle: formData.shortDescription,
          included: formData.whatsIncluded.filter((item) => item.trim() !== ''),
          excluded: formData.whatsNotIncluded.filter((item) => item.trim() !== ''),
          important_info: formData.importantInfo.filter((item) => item.trim() !== ''),
          meeting_points: formData.meetingPoints
            .filter((point) => point.title.trim() !== '')
            .map((point) => ({
              lat: point.lat,
              long: point.long,
              title: point.title,
              subtitle: point.subtitle,
              details: point.details,
              images: [],
            })),
          itinerary: formData.itineraries
            .filter((item) => item.title.trim() !== '')
            .map((item) => ({
              title: item.title,
              duration: item.duration,
              details: item.details,
              images: item.images || [],
            })),
          schedule: schedulePayload,
          adult_price: formData.pricePerPerson ? parseFloat(formData.pricePerPerson) : 0,
          child_price: formData.freeForChild
            ? 0
            : formData.pricePerChild
            ? parseFloat(formData.pricePerChild)
            : 0,
          discount_price: formData.discount ? parseFloat(formData.discount) : 0,
          is_recommended: false,
          extra_expenses: extraExpensesPayload,
          timeslots: timeslotsPayload,
        },
      ],
    }
    return payload
  }, [formData])

  const buildStep5Payload = useCallback(() => {
    return {
      settings: {
        is_instant_confirmation: formData.instantBooking,
        is_free_cancellation: formData.refundableBooking,
        is_date_changable: formData.changeDateAvailable,
      },
    }
  }, [formData])

  const handleTutorialNext = useCallback(
    (stepNumber: number) => {
      setTutorialShown((prev) => ({
        ...prev,
        [stepNumber]: true,
      }))
      // Update highest step reached when completing tutorial
      if (stepNumber > highestStepReached) {
        setHighestStepReached(stepNumber)
      }
      if (draftId) {
        MixpanelTracker.track('Activity Submission Tutorial Completed', {
          'Activity ID': draftId,
          Step: stepNumber,
        })
      }
    },
    [draftId, highestStepReached],
  )

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1
      let url: string

      if (prevStep === 1) {
        url = draftId ? `/expert-submission?draft_id=${draftId}` : '/expert-submission'
      } else {
        url = draftId
          ? `/expert-submission/step${prevStep}?draft_id=${draftId}`
          : `/expert-submission/step${prevStep}`
      }

      history.push(url)
    }
  }, [currentStep, history, draftId])

  const handleTutorialBack = useCallback(
    (stepNumber: number) => {
      setTutorialShown((prev) => ({ ...prev, [stepNumber]: false }))

      handlePrevious()
    },
    [handlePrevious],
  )

  const handleNext = useCallback(async () => {
    if (isSubmitting) return

    setIsSubmitting(true)
    setIsLoading(true)

    try {
      let activeDraftId = draftId
      if (currentStep === 1 && !activeDraftId) {
        activeDraftId = await createDraft()
        setDraftId(activeDraftId)
        setCurrentId(activeDraftId!.toString())
      }

      let payload
      let nextStepPath
      const stepToSave = currentStep

      if (currentStep === 1) {
        payload = buildStep1Payload()
        nextStepPath = `/expert-submission/step2?draft_id=${activeDraftId}`
      } else if (currentStep === 2) {
        payload = buildStep2Payload()
        nextStepPath = `/expert-submission/step3?draft_id=${draftId}`
      } else if (currentStep === 3) {
        payload = buildStep3Payload()
        nextStepPath = `/expert-submission/step4?draft_id=${draftId}`
      } else if (currentStep === 4) {
        payload = buildStep4Payload()
        nextStepPath = `/expert-submission/step5?draft_id=${draftId}`
      } else {
        setIsSubmitting(false)
        setIsLoading(false)
        return
      }

      try {
        await saveStepData(stepToSave, payload, activeDraftId)
        setTutorialShown((prev) => {
          const newState = { ...prev }
          // Mark all steps up to and including current step as shown
          for (let i = 1; i <= currentStep; i++) {
            newState[i] = true
          }
          return newState
        })

        // Update highest step reached when successfully moving to next step
        const nextStep = currentStep + 1
        if (nextStep > highestStepReached) {
          setHighestStepReached(nextStep)
        }

        let eventName = ''
        switch (stepToSave) {
          case 1:
            eventName = 'Activity Experience Step Complete'
            break
          case 2:
            eventName = 'Activity Key Information Step Complete'
            break
          case 3:
            eventName = 'Activity Overview Step Complete'
            break
          case 4:
            eventName = 'Activity Package Step Complete'
            break
        }
        if (activeDraftId && eventName) {
          MixpanelTracker.track(eventName, {
            'Activity ID': activeDraftId,
            Step: stepToSave,
          })
        }
      } catch (error: any) {
        console.error(`Error saving data for step ${currentStep}:`, error)
        message.error(`Failed to save step ${currentStep} data: ${error.message}`)
      }

      history.push(nextStepPath)
    } catch (error: any) {
      console.error('Unexpected error during form progression:', error)
      message.error(`An unexpected error occurred: ${error.message}`)
    } finally {
      setIsSubmitting(false)
      setIsLoading(false)
    }
  }, [
    currentStep,
    isSubmitting,
    draftId,
    createDraft,
    buildStep1Payload,
    buildStep2Payload,
    buildStep3Payload,
    buildStep4Payload,
    saveStepData,
    history,
    setTutorialShown,
    highestStepReached,
  ])

  const handleStepClick = useCallback(
    (stepNumber: number) => {
      let url: string

      if (stepNumber === 1) {
        url = draftId ? `/expert-submission?draft_id=${draftId}` : '/expert-submission'
      } else {
        url = draftId
          ? `/expert-submission/step${stepNumber}?draft_id=${draftId}`
          : `/expert-submission/step${stepNumber}`
      }

      history.push(url)
    },
    [history, draftId],
  )

  const handleSubmit = useCallback(async () => {
    if (!userLoggedIn?.userToken) {
      message.error('Authentication token is missing. Please log in again.')
      return
    }
    if (!draftId) {
      message.error('Draft ID is missing. Cannot submit.')
      return
    }

    setIsSubmitting(true)
    setIsLoading(true)

    try {
      const step5Payload = buildStep5Payload()
      await saveStepData(5, step5Payload)

      const publishResponse = await fetch(
        `${process.env.REACT_APP_TS_RESTFUL_API_URL}/activity/expert-submission/${draftId}/publish`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: userLoggedIn.userToken,
            project: 'haup-stable',
          },
        },
      )

      const result = await publishResponse.json()

      if (!publishResponse.ok || !result.result) {
        throw new Error(result.error?.message || 'Failed to publish activity.')
      }

      const activityId = result.data.id || 'unknown'
      sessionStorage.removeItem(`${STORAGE_PREFIX}_tutorialShown_session`)
      sessionStorage.removeItem(`${STORAGE_PREFIX}_highestStep_session`)
      sessionStorage.removeItem(`${STORAGE_PREFIX}_stepProgress`)
      sessionStorage.removeItem(`${STORAGE_PREFIX}_temp_id`)
      history.push(`/expert-submission/finish/${activityId}`)
      MixpanelTracker.track('Activity Submission Complete', {
        'Activity ID': activityId,
      })
    } catch (error: any) {
      console.error('Submission error:', error)
      message.error(`Submission failed: ${error.message}`)
    } finally {
      setIsSubmitting(false)
      setIsLoading(false)
    }
  }, [userLoggedIn?.userToken, draftId, buildStep5Payload, saveStepData, history])

  const handleSaveAndExit = useCallback(() => {
    if (isSubmitting) {
      return
    }

    Modal.confirm({
      title: 'Do you want to close this page?',
      content: 'Your information will be saved. You can return to it later.',
      okText: 'Save and exit',
      cancelText: 'Cancel',
      onOk() {
        if (draftId) {
          MixpanelTracker.track('Activity Submission Save and Exit', {
            'Activity ID': draftId,
            Step: currentStep,
          })
        }
        history.push('/submission#activities')
      },
      onCancel() {
        console.log('Cancel')
      },
    })
  }, [isSubmitting, history, draftId, currentStep])

  const isFinishPage = location.pathname.includes('/finish/')
  const shouldShowTutorial = !tutorialShown[currentStep] && currentStep >= highestStepReached

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundImage: `url('/static/media/loginbg.9f35eb74.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        backgroundColor: '#F9FAFB',
      }}
    >
      {!shouldShowTutorial && !isFinishPage && (
        <div
          style={{
            width: '100%',
            maxWidth: '500px',
            position: 'sticky',
            top: '0',
            zIndex: 10,
            backgroundColor: '#F9FAFB',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: currentStep === 1 ? 'flex-end' : 'space-between',
              marginBottom: '20px',
            }}
          >
            {currentStep !== 1 && (
              <button
                onClick={handleSaveAndExit}
                disabled={isSubmitting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#212427',
                  border: '1px solid #D6DAE0',
                  borderRadius: '20px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              >
                Save and exit
              </button>
            )}
          </div>
          <Stepper
            currentStep={currentStep}
            totalSteps={5}
            stepProgress={stepProgress}
            onStepClick={handleStepClick}
          />
        </div>
      )}

      <div style={{ width: '100%', maxWidth: '500px' }}>
        <Switch>
          <Route exact path={`${path}/step1`}>
            {shouldShowTutorial ? (
              <StepTutorial
                step={1}
                title={tutorials[1].title}
                description={tutorials[1].description}
                icon={tutorials[1].icon}
                onNext={() => handleTutorialNext(1)}
              />
            ) : (
              <Step1
                onNext={handleNext}
                onPrevious={handlePrevious}
                onStepProgress={handleStep1Progress}
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
          </Route>

          <Route exact path={`${path}/step2`}>
            {shouldShowTutorial ? (
              <StepTutorial
                step={2}
                title={tutorials[2].title}
                description={tutorials[2].description}
                icon={tutorials[2].icon}
                onNext={() => handleTutorialNext(2)}
                onBack={() => handleTutorialBack(2)}
              />
            ) : (
              <Step2
                onNext={handleNext}
                onPrevious={handlePrevious}
                onStepProgress={handleStep2Progress}
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
          </Route>

          <Route exact path={`${path}/step3`}>
            {shouldShowTutorial ? (
              <StepTutorial
                step={3}
                title={tutorials[3].title}
                description={tutorials[3].description}
                icon={tutorials[3].icon}
                onNext={() => handleTutorialNext(3)}
                onBack={() => handleTutorialBack(3)}
              />
            ) : (
              <Step3
                onNext={handleNext}
                onPrevious={handlePrevious}
                onStepProgress={handleStep3Progress}
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
          </Route>

          <Route exact path={`${path}/step4`}>
            {shouldShowTutorial ? (
              <StepTutorial
                step={4}
                title={tutorials[4].title}
                description={tutorials[4].description}
                icon={tutorials[4].icon}
                onNext={() => handleTutorialNext(4)}
                onBack={() => handleTutorialBack(4)}
              />
            ) : (
              <Step4
                onNext={handleNext}
                onPrevious={handlePrevious}
                onStepProgress={handleStep4Progress}
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
          </Route>

          <Route exact path={`${path}/step5`}>
            {shouldShowTutorial ? (
              <StepTutorial
                step={5}
                title={tutorials[5].title}
                description={tutorials[5].description}
                icon={tutorials[5].icon}
                onNext={() => handleTutorialNext(5)}
                onBack={() => handleTutorialBack(5)}
              />
            ) : (
              <Step5
                onPrevious={handlePrevious}
                onSubmit={handleSubmit}
                onStepProgress={handleStep5Progress}
                formData={formData}
                updateFormData={updateFormData}
                isSubmitting={isSubmitting}
              />
            )}
          </Route>

          <Route exact path={`${path}/finish/:activityId`}>
            <Finish />
          </Route>

          <Route exact path={path}>
            {shouldShowTutorial ? (
              <StepTutorial
                step={1}
                title={tutorials[1].title}
                description={tutorials[1].description}
                icon={tutorials[1].icon}
                onNext={() => handleTutorialNext(1)}
              />
            ) : (
              <Step1
                onNext={handleNext}
                onPrevious={handlePrevious}
                onStepProgress={handleStep1Progress}
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
          </Route>
        </Switch>
      </div>

      {(isLoading || isSubmitting) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            transition: 'opacity 0.3s ease-in-out',
            opacity: isLoading || isSubmitting ? 1 : 0,
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #0048B3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
    </div>
  )
}

export default ExpertSubmissionPage
